<template>
  <div>
    <page-header>
      <template #title>
        <h1 class="page-title">
          {{ label }}
        </h1>
      </template>
    </page-header>

    <div class="card" v-if="!loading">
      <div class="card-body">
        <selectlist
          name="type_id"
          label="Type"
          label-name="label"
          :options="jobTypes"
          :value="form.type_id"
          @input="setType"
        ></selectlist>

        <template v-if="selectedType?.name === 'third_party_movement'">
          <div class="form-group">
            <div class="col-lg-4">
              <label class="control-label"
                >What type of vehicle do you want to move?</label
              >
            </div>
            <div class="col-lg-8">
              <label class="radio-inline">
                <input
                  type="radio"
                  name="type_of_vehicle_to_move"
                  value="client"
                  v-model="form.type_of_vehicle_to_move"
                />
                Client
              </label>
              <br />
              <label class="radio-inline">
                <input
                  type="radio"
                  name="type_of_vehicle_to_move"
                  value="fleet"
                  v-model="form.type_of_vehicle_to_move"
                />
                Fleet
              </label>
            </div>
          </div>

          <incident-selector
            :value="form.incident_id"
            @input="setClaimId"
            @search="searchForClaims"
            :results="claimResults"
            :claim="claim"
            :error-message="form.errors.get('incident_id')"
            :show-error="form.errors.has('incident_id')"
            v-if="form.type_of_vehicle_to_move === 'client'"
          ></incident-selector>

          <fleet-vehicle-lookup
            v-model="form.vehicle_id"
            label="Vehicle"
            :vehicle="job?.vehicle"
            :error-message="form.errors.get('vehicle_id')"
            :show-error="form.errors.has('vehicle_id')"
            v-if="form.type_of_vehicle_to_move === 'fleet'"
          ></fleet-vehicle-lookup>
        </template>

        <template v-else-if="selectedType?.name === 'other'">
          <fleet-vehicle-lookup
            v-model="form.vehicle_id"
            label="Vehicle"
            :vehicle="job?.vehicle"
            :error-message="form.errors.get('vehicle_id')"
            :show-error="form.errors.has('vehicle_id')"
          ></fleet-vehicle-lookup>
        </template>

        <template v-else-if="typeRequiresHire">
          <incident-selector
            :value="form.incident_id"
            @input="setClaimId"
            @search="searchForClaims"
            :results="claimResults"
            :claim="claim"
            :error-message="form.errors.get('incident_id')"
            :show-error="form.errors.has('incident_id')"
          ></incident-selector>

          <selectlist
            v-model="form.hire_id"
            name="hire_id"
            label="hire"
            :options="hireResults"
            value-name="id"
            label-name="vehicle.vehicle.registration"
            :error-message="form.errors.get('hire_id')"
            :show-error="form.errors.has('hire_id')"
          ></selectlist>
        </template>

        <template v-else>
          <incident-selector
            :value="form.incident_id"
            @input="setClaimId"
            @search="searchForClaims"
            :results="claimResults"
            :claim="claim"
            :error-message="form.errors.get('incident_id')"
            :show-error="form.errors.has('incident_id')"
          ></incident-selector>
        </template>

        <datepicker-input
          name="date"
          label="Date"
          v-model="form.date"
        ></datepicker-input>

        <div class="form-group has-helper">
          <div class="col-md-4">
            <label class="control-label">Start Time:</label>
            <p class="help-block">
              Please enter the start time
            </p>
          </div>
          <div class="col-md-8">
            <vue-timepicker v-model="form.start_time"></vue-timepicker>
          </div>
        </div>

        <div class="form-group has-helper">
          <div class="col-md-4">
            <label class="control-label">End Time:</label>
            <p class="help-block">
              Please enter the end time
            </p>
          </div>
          <div class="col-md-8">
            <vue-timepicker v-model="form.end_time"></vue-timepicker>
          </div>
        </div>

        <input-block
          label="Description"
          placeholder="Description..."
          name="description"
          v-model="form.description"
        ></input-block>

        <address-lookup :address="form" @address="setAddress"></address-lookup>
      </div>

      <div class="card-footer text-right">
        <form-button :indicator="form.processing()" @clicked="submit">
          <i class="icon icon-save"></i> Save
        </form-button>
      </div>
    </div>

    <div v-else>
      <preloader></preloader>
    </div>
  </div>
</template>

<script>
import IncidentSelector from '@/components/Modules/Job/Children/IncidentSelector';
import VueTimepicker from 'vue2-timepicker';
import moment from 'moment';
import PageHeader from './../Shared/PageHeader.vue';

export default {
  props: {
    id: {
      type: String
    }
  },

  components: {
    IncidentSelector,
    VueTimepicker,
    PageHeader
  },

  data() {
    return {
      job: null,
      form: new window.Form({
        type_id: '',
        incident_id: null,
        hire_id: null,
        vehicle_id: null,
        type_of_vehicle_to_move: null,
        description: null,
        date: '',
        start_time: '',
        end_time: '',
        address_line_one: '',
        address_line_two: '',
        city: '',
        postcode: ''
      }),
      selectedType: undefined,
      claim: undefined,
      claimResults: [],
      hireResults: [],
      loading: true
    };
  },

  computed: {
    label() {
      return this.id ? 'Update Job' : 'Create Job';
    },

    jobTypes() {
      return this.$store.getters['JobTypes/all'];
    },

    jobType() {
      return this.jobTypes.find(type => type.id == this.form.type_id);
    },

    typeRequiresHire() {
      return this.jobType?.name.startsWith('hire');
    },

    address() {
      return {
        address_line_one: this.form.address_line_one,
        address_line_two: this.form.address_line_two,
        city: this.form.city,
        postcode: this.form.postcode
      };
    }
  },

  mounted() {
    this.$store.commit('JobTypes/retrieve');

    if (this.id) {
      window.Ajax.get(`/jobs/${this.id}?parts=jobable`)
        .then(({ data }) => {
          if (data.completed_at) {
            this.$router.push(`/jobs/${this.id}`);

            window.toastify.error('Oops', 'You cannot update a completed job');
            return;
          }

          this.setJob(data);

          this.loading = false;
        })
        .catch(err => {
          window.toastify.error(
            'Oops',
            'An error occurred while loading the job'
          );
        });
    } else {
      this.loading = false;
    }
  },

  methods: {
    setJob(job) {
      this.job = job;

      this.form.type_id = job.type_id;

      if (job.type_id) {
        this.setType(job.type_id);
      }

      this.setJobableData(job);
      this.setFormBasicFields(job);
    },

    setJobableData(job) {
      const typeName = this.selectedType?.name;

      if (typeName === 'third_party_movement') {
        this.handleThirdPartyMovement(job);
      } else if (typeName === 'other') {
        this.handleOtherType(job);
      } else if (this.typeRequiresHire) {
        this.handleHireType(job);
      } else {
        this.handleStandardType(job);
      }
    },

    handleThirdPartyMovement(job) {
      // If the job has a source then it is an incident, not a fleet vehicle
      const hasIncident = !!job.jobable?.source;
      const hasVehicle = job.vehicle?.id;

      if (hasIncident) {
        this.form.type_of_vehicle_to_move = 'client';
        this.claim = job.jobable?.incident || job.jobable;
        this.form.incident_id = job.jobable.id;
        this.form.vehicle_id = null;
      } else if (hasVehicle) {
        this.form.type_of_vehicle_to_move = 'fleet';
        this.form.vehicle_id = job.vehicle.id;
        this.form.incident_id = null;
      }

      this.form.hire_id = null;
    },

    handleOtherType(job) {
      this.form.vehicle_id = job.vehicle?.id || null;
      this.form.incident_id = null;
      this.form.hire_id = null;
      this.form.type_of_vehicle_to_move = null;
    },

    handleHireType(job) {
      this.claim = job.jobable?.incident;
      this.form.incident_id = job.jobable?.incident_id;
      this.form.hire_id = job.jobable?.id;
      this.form.vehicle_id = null;
      this.form.type_of_vehicle_to_move = null;
    },

    handleStandardType(job) {
      this.claim = job.jobable?.source || job.jobable;
      this.form.incident_id = job.jobable?.id;
      this.form.hire_id = null;
      this.form.vehicle_id = null;
      this.form.type_of_vehicle_to_move = null;
    },

    setFormBasicFields(job) {
      this.form.description = job.description;
      this.form.date = job.date;
      this.form.address_line_one = job.address_line_one;
      this.form.address_line_two = job.address_line_two;
      this.form.city = job.city;
      this.form.postcode = job.postcode;
      this.form.start_time = job.start_time;
      this.form.end_time = job.end_time;
    },

    setType(id) {
      this.form.type_id = id;
      this.selectedType = this.jobTypes.find(type => type.id == id);

      if (this.selectedType?.name === 'other') {
        this.form.hire_id = null;
        this.form.incident_id = null;
      } else if (this.selectedType?.name === 'third_party_movement') {
        this.type_of_vehicle_to_move = false;
        this.form.hire_id = null;
        this.form.incident_id = null;
        this.form.vehicle_id = null;
      } else {
        this.form.vehicle_id = null;
      }
    },

    searchForClaims(reference) {
      window.Ajax.get(`/incidents?filter[terms]=${reference}`)
        .then(({ data }) => {
          this.claimResults = data;
        })
        .catch(err => {
          this.claimResults = [];

          window.toastify.error(
            'Oops',
            'An error occurred while searching for claims'
          );
        });
    },

    setClaimId(id) {
      this.form.incident_id = id;

      if (this.typeRequiresHire && id) {
        window.Ajax.get(`hires?filter[incident_id]=${id}`)
          .then(({ data }) => {
            this.hireResults = data;
          })
          .catch(err => {
            this.hireResults = [];

            window.toastify.error(
              'Oops',
              'An error occurred while searching for hires'
            );
          });
      }
    },

    submit() {
      this.form.errors.clear();

      if (this.id) {
        this.update();
      } else {
        this.create();
      }
    },

    create() {
      this.formatTime();

      this.form
        .post('jobs')
        .then(({ data }) => {
          window.toastify.success('Saved!', 'The job was saved successfully');

          this.route('/jobs');
        })
        .catch(() => {
          window.toastify.error(
            'Oops',
            'An error occurred while creating the job, please check for any validation errors'
          );
        });
    },

    update() {
      this.formatTime();

      console.log(this.form.data());

      this.form
        .put(`jobs/${this.id}`)
        .then(({ data }) => {
          window.toastify.success('Saved!', 'The job was saved successfully');
        })
        .catch(() => {
          window.toastify.error(
            'Oops',
            'An error occurred while updating the job, please check for any validation errors'
          );
        });
    },

    formatTime() {
      const formatTimeString = time => {
        if (typeof time === 'string') {
          return time;
        }
        return `${time.HH}:${time.mm}`;
      };

      const formatTimeObject = time =>
        moment(formatTimeString(time), 'HH:mm').format('HH:mm:ss');

      if (this.form.start_time) {
        this.form.start_time = formatTimeObject(this.form.start_time);
      }

      if (this.form.end_time) {
        this.form.end_time = formatTimeObject(this.form.end_time);
      }
    },

    setAddress(address) {
      this.form.address_line_one = address.address_line_one;
      this.form.address_line_two = address.address_line_two;
      this.form.city = address.city;
      this.form.postcode = address.postcode;
    }
  },
  watch: {
    'form.type_of_vehicle_to_move'(newValue, oldValue) {
      // Skip if this is the initial value being set (oldValue is undefined)
      if (!oldValue) {
        return;
      }

      // Resets the relevant form values when hte type of vehicle to move radio changes
      if (newValue === 'client') {
        this.form.vehicle_id = null;
      } else if (newValue === 'fleet') {
        this.form.incident_id = null;
        this.claim = undefined;

        if (this.job?.vehicle) {
          this.job.vehicle = null;
        }
      }
    }
  }
};
</script>
